import { memoisedImport } from '../charts/echarts-lifecycle.js';
import { renderValue } from './drilldown.js';

/**
 * Mounts BlinkTable — the same component the customer's Superset dashboards render —
 * into the drilldown modal.
 *
 * The bundle is ~300KB gzipped and most sessions never open a drilldown, so it is
 * imported on first render rather than bundled into app.js. It must NOT be named in
 * vite.config.js manualChunks: naming a chunk makes Rollup treat it as an entry and
 * pulls it back into the eager graph, which is the thing this defers.
 *
 * It loads the SOLO shape deliberately. The build also emits split/superset-table.js, which shares
 * chunks with the charts entry, and that sharing is a loss for a page wanting only a table:
 * esbuild's shared chunk holds the UNION of both entries' imports, so the split table costs
 * 432KB gzipped against the solo table's 312KB. A page that draws charts should load the
 * split pair; this one never draws charts.
 */
const loadBlinkTable = memoisedImport(() => import('../vendor/blink-superset/solo/superset-table.js'));

/**
 * The drilldown describes each column by the FILTER widget it gets, which is a finer
 * distinction than a renderer needs (a date range and a timestamp are both temporal).
 * Collapsing here keeps DrilldownService the single place that classifies a BigQuery
 * type; an unrecognised filter falls back to string, which renders any value safely.
 */
const TYPE_BY_FILTER = {
    text: 'string',
    select: 'string',
    boolean: 'boolean',
    numberRange: 'number',
    dateRange: 'temporal',
};

const SCROLLABLE_OVERFLOW = /^(auto|scroll)$/;

/**
 * The page size the drilldown has always shown. Held here, not read from the filter
 * component's scope: that component keeps no paging state, and BlinkTable falls back to
 * 25 rows a page, without any error, when it is handed undefined.
 */
const DRILLDOWN_PAGE_SIZE = 10;

/**
 * Applied to the promoted scroller. Every utility here is already emitted for
 * drilldown-table.blade.php, and resources/js/**\/*.js is in tailwind.config.js's content
 * globs, so nothing needs safelisting.
 */
const SCROLLER_FOCUS_CLASSES = [
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-blink',
    'focus-visible:ring-inset',
];

export default function DrilldownBlinkTable({ scrollRegionLabel = '' } = {}) {
    return {
        blinkTableFailed: false,

        /** The element the React tree is mounted into, kept for teardown. */
        _mountedOn: null,

        /** BlinkTable's own scroll port, once promoted to a tab stop. */
        _scroller: null,

        _resizeObserver: null,

        renderBlinkTable(element, rows, columns) {
            this._mountedOn = element;

            if (!this._resizeObserver && typeof ResizeObserver !== 'undefined') {
                this._resizeObserver = new ResizeObserver(() => this.syncScrollRegion());
                this._resizeObserver.observe(element);
            }

            loadBlinkTable()
                .then(({ mountBlinkTable }) => {
                    mountBlinkTable(element, {
                        columns: (columns || []).map(column => ({
                            key: column.key,
                            label: column.label,
                            type: TYPE_BY_FILTER[column?.filter?.type] || 'string',
                            decimals: column.decimals ?? null,
                        })),
                        rows: rows || [],
                        options: {
                            pageSize: DRILLDOWN_PAGE_SIZE,
                            // BlinkTable owns the search, so the drilldown looks and behaves
                            // like the same table on a dashboard. It debounces at 200ms, which
                            // is if anything quicker than the 250ms Blade box it replaces.
                            includeSearch: true,
                            showCellBars: false,
                            // Drilldown rows are raw customer data out of BigQuery, never markup.
                            allowRenderHtml: false,
                            height: Math.max(240, Math.round(window.innerHeight * 0.5)),
                            // The component's own empty state is untranslated English;
                            // this is the same string the replaced markup rendered.
                            emptyMessage: this.strings?.noResults,
                            noMatchesMessage: this.strings?.noMatches,
                            // BlinkTable has no words for a boolean and prints the raw literal,
                            // so a yes/no column read true/false beneath a filter offering Yes/No.
                            // Matched on the value, not the column type: a BOOL column a metric
                            // pre-filters arrives as a select, typed string. Every other cell gets
                            // null and keeps BlinkTable's own formatting.
                            // BlinkTable's search box matches a described cell by this text, so a
                            // cell reading Yes is found by typing "yes", and "true" finds nothing.
                            describeCell: (row, key) => (typeof row?.[key] === 'boolean'
                                ? { text: renderValue(row[key], null, this.strings) }
                                : null),
                        },
                    });
                    this.blinkTableFailed = false;
                    this.syncScrollRegion();
                })
                .catch(error => {
                    console.error('BlinkTable failed to load', error);
                    this.blinkTableFailed = true;
                });
        },

        /**
         * The tab stop has to sit on the element that actually scrolls. BlinkTable's sticky
         * layout renders that element itself (a div with overflow:auto wrapping the body
         * table), so the host can only reach it after mount -- and only while it overflows: a
         * permanent tab stop on a table that fits is a stop that does nothing.
         *
         * Setting tabindex/aria-label suppresses that div's role="presentation" (ARIA ignores
         * the role on a focusable or aria-labelled element), so both come back off the moment
         * the overflow goes away.
         */
        syncScrollRegion() {
            const element = this._mountedOn;
            if (!element) return;

            // React sizes the columns in a layout effect, so scrollWidth only means anything
            // once the browser has laid the new table out.
            requestAnimationFrame(() => {
                if (this._mountedOn !== element) return;

                const scroller = this._findScroller(element);
                if (this._scroller && this._scroller !== scroller) this._demoteScroller(this._scroller);
                this._scroller = scroller;
                if (!scroller) return;

                const overflows = scroller.scrollWidth > scroller.clientWidth
                    || scroller.scrollHeight > scroller.clientHeight;

                if (overflows) {
                    scroller.setAttribute('tabindex', '0');
                    if (scrollRegionLabel) scroller.setAttribute('aria-label', scrollRegionLabel);
                    scroller.classList.add(...SCROLLER_FOCUS_CLASSES);
                } else if (document.activeElement !== scroller) {
                    // Demoting the element the user is standing on would drop focus to <body>.
                    this._demoteScroller(scroller);
                }
            });
        },

        _demoteScroller(scroller) {
            scroller.removeAttribute('tabindex');
            scroller.removeAttribute('aria-label');
            scroller.classList.remove(...SCROLLER_FOCUS_CLASSES);
        },

        /**
         * The scroll port is the one overflow:auto div wrapping a <table>. The sticky header
         * and footer clones are overflow:hidden, and the measuring clone is visibility:hidden.
         */
        _findScroller(element) {
            for (const candidate of element.querySelectorAll('div')) {
                if (candidate.firstElementChild?.tagName !== 'TABLE') continue;
                const style = window.getComputedStyle(candidate);
                if (style.visibility === 'hidden') continue;
                if (!SCROLLABLE_OVERFLOW.test(style.overflowX)
                    && !SCROLLABLE_OVERFLOW.test(style.overflowY)) continue;
                return candidate;
            }
            return null;
        },

        /**
         * Alpine calls this when the element leaves the DOM. Without it the React tree
         * survives the modal closing, and reopening the drilldown on another metric
         * would show the previous metric's rows until the new ones arrived.
         */
        destroy() {
            this._resizeObserver?.disconnect();
            this._resizeObserver = null;
            this._scroller = null;

            const element = this._mountedOn;
            if (!element) return;
            this._mountedOn = null;
            loadBlinkTable()
                .then(({ unmountBlinkTable }) => unmountBlinkTable(element))
                .catch(() => {});
        },
    };
}
